import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { RecaptchaModule } from 'ng-recaptcha';
import { environment } from 'src/environments/environment';
import { RecaptchaService } from '../../core/services/recaptcha.service';
import { TicketService } from '../../core/services/ticket.service';
import { catchError, finalize, tap } from 'rxjs/operators';
import { firstValueFrom, of } from 'rxjs';
import { SharedModule } from 'src/app/shared/shared.module';
import { OperationsService } from 'src/app/core/services/operations.service';

type DocType = '1' | '2' | '3' | '4' | '5';

@Component({
  selector: 'app-resend-tickets',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatMomentDateModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatSnackBarModule,
    RecaptchaModule,
    SharedModule
  ],
  templateUrl: './resend-tickets.component.html'
})
export class ResendTicketsComponent {
  private fb = inject(FormBuilder);
  private recaptchaService = inject(RecaptchaService);
  private ticketService = inject(TicketService);
  private operationService = inject(OperationsService);
  private snackBar = inject(MatSnackBar);

  recaptchaSiteKey = environment.recaptchaSiteKey;
  loading = signal(false);
  recaptchaError = signal<string>('');
  ticket = signal<any>(null);
  busqueda = signal<boolean>(false);
  sending = signal<boolean>(false);
  downloading = signal<boolean>(false);
  finished = signal<boolean>(false);

  docTypes: { value: DocType; label: string }[] = [
    { value: '1', label: 'D.N.I.' },
    { value: '2', label: 'C.U.I.T.' },
    { value: '3', label: 'Pasaporte' },
    { value: '4', label: 'Libreta Cívica' },
    { value: '5', label: 'Cédula de Identidad' },
  ];

  form = this.fb.group({
    docType: this.fb.control<DocType | null>(null, { validators: [Validators.required] }),
    docNumber: this.fb.control('', { validators: [Validators.required, Validators.pattern(/^\d{6,12}$/)] }),
    travelDate: this.fb.control<Date | null>(null, { validators: [Validators.required] }),
    purchaseEmail: this.fb.control('', { validators: [Validators.required, Validators.email] }),
    destinationEmail: this.fb.control('', { validators: [Validators.required, Validators.email] }),
  });

  // Variable separada para el token del reCAPTCHA
  recaptchaToken = signal<string | null>(null);

  isInvalid(name: string): boolean {
    const c = this.form.get(name);
    return !!c && c.invalid && (c.dirty || c.touched);
  }

  onRecaptchaResolved(token: string | null): void {
    this.recaptchaToken.set(token);
    this.recaptchaError.set('');
  }

  onRecaptchaError(): void {
    this.recaptchaError.set('Error al completar el reCAPTCHA. Intentá nuevamente.');
    this.recaptchaToken.set(null);
  }

  async submit(): Promise<void> {
    this.ticket.set(null);
    if (this.form.invalid || !this.recaptchaToken()) {
      this.form.markAllAsTouched();
      if (!this.recaptchaToken()) {
        this.recaptchaError.set('Completá el reCAPTCHA para continuar.');
      }
      return;
    }

    this.loading.set(true);
    this.recaptchaError.set('');

    const formData = this.form.getRawValue();
    const recaptchaToken = this.recaptchaToken();

    if (!recaptchaToken) {
      this.recaptchaError.set('Completá el reCAPTCHA para continuar.');
      this.loading.set(false);
      return;
    }

    try {
      // Verificar el reCAPTCHA primero
      const verificationResult = await this.recaptchaService.verifyToken(recaptchaToken, 'resend_tickets')
        .pipe(
          tap(response => {
            if (!this.recaptchaService.isValid(response)) {
              const errors = this.recaptchaService.getErrorMessages(response);
              throw new Error(errors.join(', ') || 'Verificación del reCAPTCHA falló');
            }
          }),
          catchError(error => {
            console.error('Error verificando reCAPTCHA:', error);
            throw new Error(error.message || 'Error al verificar el reCAPTCHA');
          })
        ).toPromise();

      // Si la verificación es exitosa, proceder con el envío
      const payload = {
        departure_datetime: formData.travelDate?.toISOString().split('T')[0] || '', // Formato YYYY-MM-DD
        document_type_id: formData.docType!,
        document_number: formData.docNumber!,
        email: formData.purchaseEmail!,
      };
      this.loading.set(true);
      this.busqueda.set(true);
      this.finished.set(false);

      // Llamar al servicio HTTP para reenviar los pasajes
      this.ticketService.searchTickets(payload).subscribe({
        next: (response: any) => {
          this.loading.set(false);
          if (response.data && response.data.length === 0) {
            this.ticket.set(null);
            this.snackBar.open('No se encontró ningún pasaje con los datos proporcionados.', 'Cerrar', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
          } else {
            this.ticket.set(response);
          }
          this.finished.set(true);
        },
        error: (error: any) => {
          console.error('Error al reenviar tickets:', error);
          this.loading.set(false);
          this.ticket.set(null);
          const errorMessage = error.error?.message || 'Error al reenviar los tickets';
          this.snackBar.open(errorMessage, 'Cerrar', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
          this.finished.set(true);
        }
      });

    } catch (error: unknown) {
      console.error('Error en submit:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error al procesar la solicitud';
      this.recaptchaError.set(errorMessage);
      this.snackBar.open(errorMessage, 'Cerrar', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
      this.finished.set(true);
    } finally {
      // El loading se maneja en los callbacks de la suscripción
    }
  }

  async reenviarTicket(ticket: any) {
    this.sending.set(true);
    const payload = {
      to: this.form.get('destinationEmail')?.value,
      subject: 'Reenvío de pasaje',
      message: 'Estimado, te enviamos el pasaje de la operación ' + ticket.operations[0].id + ' con el código ' + ticket.code + ' para que puedas imprimirlo.',
    }
    //const response = await firstValueFrom(this.operationService.enviarBoletosPorEmail(ticket.operations[0].id));
    const responseOtroMail = await firstValueFrom(this.operationService.enviarBoletosPorEmailOtroMail(ticket.operations[0].id, this.form.get('destinationEmail')?.value || ''));
    //console.log('Response:', response);
    console.log('ResponseOtroMail:', responseOtroMail);
    if (responseOtroMail.status === 200) {
      this.sending.set(false);
      this.snackBar.open('Boletos enviados por email', 'Cerrar', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
    } else {
      this.sending.set(false);
      this.snackBar.open('Ocurrió un error al enviar boletos por email, volve a intentarlo o contacta al soporte.', 'Cerrar', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
    }
    this.sending.set(false);
/*
    this.ticketService.resendTicket(ticket.id, ticket.operations[0].id, payload).subscribe({
      next: (response: any) => {
        console.log('Ticket reenviado:', response);
        this.sending.set(false);
      },
      error: (error: any) => {
        console.error('Error al reenviar ticket:', error);
        this.sending.set(false);
      }
    });
    */
  }

  async downloadTicket(ticket: any) {
    this.downloading.set(true);
    try {
      const urlpdf = `${environment.backendApiUrl}/operations/${ticket.operations[0].id}/tickets/${ticket.id}/pdf`;
      const response = await fetch(urlpdf);
      if (!response.ok) {
        throw new Error('Error al descargar boletos');
      }
      const fileName = `boletos_${ticket.operations[0].id}_${ticket.id}.pdf`;
      const a = document.createElement('a');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error al descargar boletos:', err);
    } finally {
      this.downloading.set(false);
    }
  }
}
